import clsx from "clsx";
import { forwardRef } from "react";
import { useController } from "react-hook-form";

import { ReactComponent as RadioIcon } from "assets/icons/radio.svg";

const RadioGroup = ({
  required,
  name,
  options,
  className,
}: {
  label?: string;
  required?: boolean;
  name: string;
  options: Array<{ label: string; value: number | string }>;
  className?: string;
}) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    rules: { required },
    defaultValue: options[0].value,
  });

  const onChange = (value: number | string) => {
    field.onChange(value);
    field.onBlur();
  };

  return (
    <ul className={clsx("flex gap-[1.875rem]", className)}>
      {options.map((option) => (
        <li className="inline-flex" key={option.value}>
          <Radio
            label={option.label}
            checked={field.value === option.value}
            onChange={() => {
              onChange(option.value);
            }}
            error={!!error}
          />
        </li>
      ))}
    </ul>
  );
};

const Radio = forwardRef<
  HTMLInputElement,
  {
    label?: string;
    checked?: boolean;
    onChange?: () => void;
    className?: string;
    error?: boolean;
  }
>(({ label, checked, onChange, className, error }, ref) => {
  return (
    <label
      className={clsx(
        "relative h-6 inline-flex items-center flex-row-reverse gap-[0.625rem]",
        className,
      )}
    >
      {label}
      <input
        ref={ref}
        className="absolute w-0 h-0 opacity-0 cursor-pointer peer"
        type="radio"
        checked={checked}
        onChange={onChange}
      />
      <span className="size-6 inline-flex justify-center items-center">
        <RadioIcon
          className="size-5"
          fill={error ? "#EC3434" : "#667080"}
          color={checked ? "#667080" : "white"}
        />
      </span>
    </label>
  );
});

Radio.displayName = "Radio";

export default RadioGroup;
